"use client"

import React, { useTransition } from "react"
import { useRouter } from "next/navigation"
import { updateBusinessUserProfileAction } from "@/actions/accountUserActions"
import { zodResolver } from "@hookform/resolvers/zod"
import { format, isValid, parse } from "date-fns"
import { de, fr, nl } from "date-fns/locale"
import { CalendarIcon, CheckCircleIcon, LoaderCircle } from "lucide-react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { AccountContext } from "@/lib/storefront/account/account-service"
import { cn } from "@/lib/utils"
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
  RadioGroupItem,
  Form as RHFForm,
} from "@/components/ui/components"
import { Button } from "@/components/ui/components/button"
import { Calendar } from "@/components/ui/components/calendar"
import { Checkbox } from "@/components/ui/components/checkbox"
import { Input } from "@/components/ui/components/input"
import { Label } from "@/components/ui/components/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/components/select"
import { normalizeBirthday } from "@/components/account/profile/personal/normalizeBirthday"
import { FormRow } from "@/components/account/shared/FormRow"

const profileSchema = z.object({
  salutation: z.string().min(1, "salutation_invalid"),
  firstName: z.string().min(1, "firstName_invalid"),
  lastName: z.string().min(1, "lastName_invalid"),
  department: z.string().max(200).optional().or(z.literal("")),
  active: z.boolean().optional(),
  phone: z.string().max(50).optional().or(z.literal("")),
  email: z.string().min(1),
  businessPartnerNo: z.string().min(1),
  fax: z.string().max(50).optional().or(z.literal("")),
  language: z.enum(["de_DE", "fr_FR", "nl_NL"]).optional(),
  birthday: z
    .string()
    .optional()
    .transform((v) => (v ?? "").trim())
    .refine((v) => v === "" || /^\d{2}\.\d{2}\.\d{4}$/.test(v), {
      message: "birthday_invalid",
    })
    .refine(
      (v) => {
        if (!v) return true
        const d = parse(v, "dd.MM.yyyy", new Date())
        return isValid(d) && d <= new Date()
      },
      {
        message: "birthday_invalid",
      }
    ),
})

export type ProfileFormValues = z.infer<typeof profileSchema>

export function AccountUserEditProfileForm({
  accountContext,
  customerNo,
  userId,
  initialValues,
  blok,
}: {
  accountContext: AccountContext
  customerNo: string
  userId: string
  initialValues: ProfileFormValues
  blok?: any
}) {
  const [isPending, startTransition] = useTransition()
  const router = useRouter()

  const form = useForm<ProfileFormValues>({
    defaultValues: { ...initialValues, birthday: normalizeBirthday(initialValues?.birthday) },
    resolver: zodResolver(profileSchema),
    mode: "onChange",
  })
  const { control, formState, register, handleSubmit } = form

  async function onSave(data: ProfileFormValues) {
    startTransition(async () => {
      const res = await updateBusinessUserProfileAction(customerNo, userId, data)
      if ((res as any)?.error) {
        toast.error(blok?.text_error ?? "")
      } else {
        toast.success(blok?.text_success ?? "", {
          icon: <CheckCircleIcon className={"size-4 text-green-500"} />,
          description: "",
        })
        setTimeout(() => {
          window.location.href = `/account/users/${encodeURIComponent(userId)}`
        }, 1000)
      }
    })
  }
  const localeCalender = accountContext.isoLocale == "fr_FR" ? fr : accountContext.isoLocale == "nl_NL" ? nl : de

  return (
    <RHFForm {...form}>
      <form onSubmit={handleSubmit(onSave)} className="flex flex-col gap-2 lg:gap-6">
        <FormRow label={blok.label_salutation} htmlFor={"salutation"} required>
          <Controller
            control={control}
            name="salutation"
            render={({ field }) => (
              <RadioGroup
                onValueChange={field.onChange}
                value={field.value}
                name={field.name}
                className={cn(
                  "flex flex-row justify-between gap-1 rounded-lg border  border-gray-300 bg-white px-5 py-3",
                  { "border-primary": formState.errors.salutation }
                )}
              >
                <div className="flex w-fit items-center space-x-2 text-base outline-none ring-0 hover:border-black">
                  <RadioGroupItem value={blok.salutation_female ?? ""} id="r1" />
                  <Label className={"font-bold"} htmlFor="r1">
                    {blok.salutation_female}
                  </Label>
                </div>
                <div className="flex w-fit items-center space-x-2 text-base outline-none ring-0 hover:border-black">
                  <RadioGroupItem value={blok.salutation_male ?? ""} id="r2" />
                  <Label className={"font-bold"} htmlFor="r2">
                    {blok.salutation_male}
                  </Label>
                </div>
                <div className="flex w-fit items-center space-x-2 text-base outline-none ring-0 hover:border-black">
                  <RadioGroupItem value={blok.salutation_divers ?? ""} id="r3" />
                  <Label className={"font-bold"} htmlFor="r3">
                    {blok.salutation_divers}
                  </Label>
                </div>
              </RadioGroup>
            )}
          />
          {formState.errors.salutation && (
            <p className={"mt-1 text-xs text-red-600"} role="alert">
              {blok.salutation_missing}
            </p>
          )}
        </FormRow>

        <FormRow label={blok?.label_first_name} htmlFor="firstName" required>
          <Input id="firstName" {...register("firstName")} />
          {formState.errors.firstName && <p className="mt-1 text-xs text-red-600">{blok?.first_name_required}</p>}
        </FormRow>

        <FormRow label={blok?.label_last_name} htmlFor="lastName" required>
          <Input id="lastName" {...register("lastName")} />
          {formState.errors.lastName && <p className="mt-1 text-xs text-red-600">{blok?.last_name_required}</p>}
        </FormRow>

        <FormRow label={blok?.label_department} htmlFor="department">
          <Input id="department" {...register("department")} />
        </FormRow>

        <div className="grid grid-cols-1 items-start gap-2 md:grid-cols-3 md:gap-8">
          <div />
          <div className="flex items-center space-x-2 md:col-span-2">
            <FormField
              control={control}
              name="active"
              render={({ field }) => (
                <>
                  <Checkbox id="active" checked={!!field.value} onCheckedChange={(v) => field.onChange(!!v)} />
                  <Label htmlFor="active">{blok?.label_active}</Label>
                </>
              )}
            />
          </div>
        </div>

        <FormRow label={blok?.label_phone} htmlFor="phone">
          <Input id="phone" type="tel" {...register("phone")} />
        </FormRow>

        <FormRow label={blok?.label_fax} htmlFor="fax">
          <Input id="fax" {...register("fax")} />
        </FormRow>

        <FormRow label={blok?.label_birthday} htmlFor={"birthday"}>
          <FormField
            control={control}
            name="birthday"
            render={({ field }) => {
              const parsed = field.value ? parse(field.value as string, "dd.MM.yyyy", new Date()) : undefined
              const selectedDate = parsed && isValid(parsed) ? parsed : undefined
              return (
                <FormItem className="flex flex-col">
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          size={"defaultNoGrow"}
                          className={cn(
                            "w-full border-gray-200 pl-3 text-left text-sm font-normal text-black",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {selectedDate ? (
                            format(selectedDate, "PPP", { locale: localeCalender })
                          ) : (
                            <span>{blok.label_birthday_choose_date}</span>
                          )}
                          <CalendarIcon className="ml-auto size-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => field.onChange(date ? format(date, "dd.MM.yyyy") : "")}
                        disabled={(date) => date > new Date()}
                        captionLayout="dropdown"
                        locale={localeCalender}
                      />
                    </PopoverContent>
                  </Popover>
                  <input type="hidden" name={field.name} value={field.value ?? ""} />
                  <FormMessage />
                </FormItem>
              )
            }}
          />

          {formState.errors.birthday && (
            <p className={"text-sm text-primary"} role="alert">
              {blok?.birthday_invalid}
            </p>
          )}
        </FormRow>

        <FormRow label={blok?.label_preferred_language} htmlFor="language" required>
          <FormField
            control={control}
            name="language"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>

                  <SelectContent>
                    {accountContext.preferredLanguages.map((language) => (
                      <SelectItem key={language} value={language}>
                        {blok["label_language_" + language]}
                      </SelectItem>
                    ))}
                  </SelectContent>

                </SelectContent>
              </Select>
            )}
          />
        </FormRow>

        <div className="flex justify-end gap-4 pt-8">
          <Button
            type="reset"
            variant="outline"
            onClick={() => router.replace(`/account/users/${encodeURIComponent(userId)}`)}
          >
            {blok.label_cancel}
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? <LoaderCircle className={"animate-spin"} /> : blok?.label_save_changes}
          </Button>
        </div>
      </form>
    </RHFForm>
  )
}
